import rtlPlugin from "stylis-plugin-rtl";
import { useState, useEffect } from "react";
import { CacheProvider } from "@emotion/react";
import { styled } from "@mui/material";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import authStore from "@/stores/authStore";
import MessagesFilter from "@/components/messages/messagesFilter";
import popupService from "@/services/popupService";
import api from "@/services/api";
import "./messagesStyles.css";
import { MessagesFilterRequest, MessageItem } from "@/models/apiModels";
import InfiniteScroll from "react-infinite-scroll-component";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/util";

import CreateEditMessageDialog from "./createEditMessageDialog";

import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import LightGreenButtonStyled from "../styled/light.green.button.styled";
import PlusElement from "@/assets/images/PlusElement.svg";
import BaseButton from "@/components/common/baseButton";
import TotalCountComponent from "@/components/common/resultCountComponent";

function Row(props: { row: MessageItem; openEditDialog: any; setCurrentMessageId: any }) {
    const { row, openEditDialog, setCurrentMessageId } = props;
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();

    const handleClickOpenDialog = (messageId: number) => {
        setCurrentMessageId(messageId);
        openEditDialog(true);
    };

    return (
        <>
            <TableRow
                sx={{
                    "& > *": {
                        borderBottom: open ? "unset" : "1px solid rgba(64, 82, 137, 0.25)",
                        borderTop: "1px solid rgba(64, 82, 137, 0.25)",
                        borderBottomLeftRadius: open ? "0 !important" : "",
                        borderBottomRightRadius: open ? "0 !important" : "",
                    },
                }}
            >
                <TableCell>
                    <div>{row.id}</div>
                </TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell>{t(`messages.sourceDestination.${row.targetType}`)}</TableCell>
                <TableCell>
                    <div className="table-number-format-name">{formatDate(row.createdAt)}</div>
                </TableCell>
                <TableCell>
                    <div className="ellipsis message-content">
                        <span>{row.content}</span>
                    </div>
                </TableCell>
                <TableCell>
                    {row.active ? (
                        <div className="status-element">
                            <div className="status-color-active"></div>
                            <div>{t("message.ActiveStatus")}</div>
                        </div>
                    ) : (
                        <div className="status-element">
                            <div className="status-color-inactive"></div>
                            <div>{t("message.InactiveStatus")}</div>
                        </div>
                    )}
                </TableCell>
                <TableCell>
                    <DetailsBtn aria-label="expand row" className="basic-button" onClick={() => setOpen(!open)}>
                        {t("messages.button.details")}
                    </DetailsBtn>
                </TableCell>
            </TableRow>
            <TableRow
                sx={{
                    "& > *": {
                        borderBottom: open ? "1px solid rgba(64, 82, 137, 0.25)" : "unset",
                    },
                }}
            >
                <TableCell className="collapseble-cell" style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <div className="message-details-container">
                            <div className="message-details-content-message">
                                <div className="title">{t("messages.content")}</div>
                                <div>{row.content}</div>
                            </div>
                            <div className="message-details-edit-buttons">
                                <LightGreenButtonStyled onClick={() => handleClickOpenDialog(row.id)}>
                                    {t("button.edit")}
                                </LightGreenButtonStyled>
                            </div>
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const DetailsBtn = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
          }

          &.${buttonUnstyledClasses.root} {
            width: 85px;
            height: 26px;
            font-size: 14px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
          }`
);

const Messages = () => {
    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);
    const [messages, setMessages] = useState<MessageItem[]>([]);
    const [messagesFilter, setMessagesFilter] = useState<MessagesFilterRequest>({
        keywords: "",
        providerName: "",
        fromDate: null,
        toDate: null,
        messageStatus: null,
        orderAsc: false,
        orderBy: "",
        pageIndex: 0,
    });
    const [showMessagesTable, setShowMessagesTable] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentMessageId, setCurrentMessageId] = useState(null);

    const { t } = useTranslation();

    const onSearchClick = async () => {
        setMessages([]);
        await setShowMessagesTable(false);
        if (messagesFilter.pageIndex !== 0) {
            setPageIndex(0);
        } else {
            startFetching();
        }
        setShowMessagesTable(true);
    };

    const setPageIndex = (index?: number) => {
        setMessagesFilter((prevData) => ({
            ...prevData,
            pageIndex: index ?? messagesFilter.pageIndex + 1,
        }));
    };

    function getArrowClass(sort: string) {
        if (sort === messagesFilter.orderBy) {
            if (messagesFilter.orderAsc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    const sortBy = (sortBy: string) => {
        setMessages([]);
        if (messagesFilter.orderBy !== sortBy) {
            setMessagesFilter((prevData) => ({
                ...prevData,
                orderAsc: true,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        } else {
            setMessagesFilter((prevData) => ({
                ...prevData,
                orderAsc: !messagesFilter.orderAsc,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        }
    };

    let startFetchingBusy = false;
    const startFetching = async () => {
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            const response = await api.getMessages(messagesFilter);
            if (messagesFilter.pageIndex === 0) {
                setTotal(response.data.total);
                setTotalFilter(response.data.totalFilter);
                setMessages(response.data.messages);
            } else {
                setMessages((oldArray) => [...oldArray, ...response.data.messages]);
            }
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messagesFilter.pageIndex, messagesFilter.orderBy, messagesFilter.orderAsc]);

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const createMessage = () => {
        setCurrentMessageId(null);
        setOpenDialog(true);
    };

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <div className="accounting-container">
                <div className="messages-header-container">
                    <div className="employee-left-element font-style"></div>
                    <div className="employee-right-element">
                        <BaseButton className="basic-button" onClick={createMessage} type="button">
                            <div>{t("messages.newMessage")}</div>
                            <img src={PlusElement} alt="PlusElement" />
                        </BaseButton>
                    </div>
                </div>
                <MessagesFilter
                    searchClick={onSearchClick}
                    total={total}
                    filter={messagesFilter}
                    setFilter={setMessagesFilter}
                />
                <TotalCountComponent
                    title={t("messages.QuantityOfMessages") as string}
                    allCount={total}
                    currentCount={totalFilter}
                />
                {showMessagesTable ? (
                    <div className="messages-container">
                        <CreateEditMessageDialog
                            messageId={currentMessageId}
                            open={openDialog}
                            handleClose={handleCloseDialog}
                            handleSearch={onSearchClick}
                        ></CreateEditMessageDialog>
                        <TableContainer className="messages-table">
                            <InfiniteScroll
                                dataLength={messages.length}
                                next={setPageIndex}
                                hasMore={messages.length < total}
                                loader={false}
                                scrollableTarget="orders-list-grid"
                            >
                                <Table stickyHeader aria-label="sticky collapsible table">
                                    <TableHead sx={{ borderRadius: "12px" }}>
                                        <TableRow>
                                            <TableCell
                                                style={{ width: "80px" }}
                                                className="cursor-pointer"
                                                onClick={() => sortBy("Id")}
                                            >
                                                <div>
                                                    {t("messages.number")}
                                                    <i className={`icon-f ${getArrowClass("Id")} fs-12`} />
                                                </div>
                                            </TableCell>
                                            <TableCell className="cursor-pointer" onClick={() => sortBy("Title")}>
                                                <div>
                                                    {t("messages.title")}
                                                    <i className={`icon-f ${getArrowClass("Title")} fs-12`} />
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                style={{ width: "180px" }}
                                                className="cursor-pointer"
                                                onClick={() => sortBy("DestinationType")}
                                            >
                                                <div>
                                                    {t("messages.destinataionType")}
                                                    <i className={`icon-f ${getArrowClass("DestinationType")} fs-12`} />
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                style={{ width: "100px" }}
                                                className="cursor-pointer"
                                                onClick={() => sortBy("CreationDate")}
                                            >
                                                <div className="table-number-format-name">
                                                    {t("messages.creationDate")}
                                                    <i className={`icon-f ${getArrowClass("CreationDate")} fs-12`} />
                                                </div>
                                            </TableCell>
                                            <TableCell style={{ width: "40%" }}>{t("messages.content")}</TableCell>
                                            <TableCell
                                                style={{ width: "100px" }}
                                                className="cursor-pointer"
                                                onClick={() => sortBy("Status")}
                                            >
                                                <div>
                                                    {t("messages.status")}
                                                    <i className={`icon-f ${getArrowClass("Status")} fs-12`} />
                                                </div>
                                            </TableCell>
                                            <TableCell style={{ width: "70px" }}>{t("messages.details")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {messages &&
                                            messages.map((row) => (
                                                <Row
                                                    key={row.id}
                                                    row={row}
                                                    openEditDialog={setOpenDialog}
                                                    setCurrentMessageId={setCurrentMessageId}
                                                />
                                            ))}
                                    </TableBody>
                                </Table>
                            </InfiniteScroll>
                        </TableContainer>
                    </div>
                ) : (
                    ""
                )}
            </div>
        </CacheProvider>
    );
};

export default Messages;
